import { useState } from 'react';
import { Alert, Button, Snackbar, Box } from '@mui/material';
import { Email as EmailIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { useAuth } from '@hooks/useAuth';
import { sendVerificationEmail, reloadUser } from '@services/authService';

const EmailVerificationBanner = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Only show for email/password users who haven't verified
  if (!currentUser || currentUser.emailVerified || currentUser.providerData[0]?.providerId === 'google.com') {
    return null;
  }

  const handleResendEmail = async () => {
    try {
      setLoading(true);
      await sendVerificationEmail();
      setSnackbarMessage('Verification email sent! Please check your inbox.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage(error.message || 'Failed to send verification email.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckVerification = async () => {
    try {
      setLoading(true);
      await reloadUser();

      // Check the updated status
      if (currentUser.emailVerified) {
        setSnackbarMessage('Email verified successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        // Reload the page to update the UI
        setTimeout(() => window.location.reload(), 1500);
      } else {
        setSnackbarMessage('Email not verified yet. Please check your inbox and click the verification link.');
        setSnackbarSeverity('info');
        setSnackbarOpen(true);
      }
    } catch (error) {
      setSnackbarMessage('Failed to check verification status.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Alert
        severity="warning"
        icon={<EmailIcon />}
        sx={{ mb: 3 }}
        action={
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              color="inherit"
              size="small"
              onClick={handleResendEmail}
              disabled={loading}
            >
              Resend Email
            </Button>
            <Button
              color="inherit"
              size="small"
              onClick={handleCheckVerification}
              disabled={loading}
              startIcon={<CheckCircleIcon />}
            >
              I've Verified
            </Button>
          </Box>
        }
      >
        <strong>Email Verification Required</strong>
        <br />
        Please verify your email address ({currentUser.email}). Check your inbox for the verification link.
      </Alert>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EmailVerificationBanner;
